import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiPost, apiGet } from 'api'
import AuthContext from 'context/auth-context'

const getDashboardTemplates = async (token, params) => {
  const url = `sections/${params.section_id}/templates`
  const data = await apiGet({ url, params, token })
  return data
}

export function useDashboardTemplates({ params, options }) {
  const token = useContext(AuthContext)
  const queryFn = () => getDashboardTemplates(token, params)
  const queryKey = ['dashboardTemplates', params]

  return useQuery(queryKey, queryFn, {
    enabled: !!params?.section_id,
    ...options
  })
}

const getDashboardTemplate = async (token, id) => {
  const url = `templates/${id}`
  const data = await apiGet({ url, token })
  return data
}

export function useDashboardTemplate({ id, options }) {
  const token = useContext(AuthContext)
  const queryFn = () => getDashboardTemplate(token, id)
  const queryKey = [`templates/${id}`]

  return useQuery(queryKey, queryFn, {
    enabled: !!id,
    ...options
  })
}

// folder_id rides beside user_filter_values, not inside it: the controller permit!s that hash, so a
// folder id in there would go unchecked. See BuildsController.
const applyDashboardTemplate = async (token, templateId, userFilterValues = {}, folderId = null) => {
  const url = `templates/${templateId}/builds`
  const data = await apiPost({
    url,
    token,
    objectName: 'dashboard',
    params: {
      user_filter_values: userFilterValues,
      folder_id: folderId
    }
  })
  return data
}

export function useApplyDashboardTemplate({ options }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    ({ templateId, userFilterValues = {}, folderId = null }) => {
      return applyDashboardTemplate(token, templateId, userFilterValues, folderId)
    },
    {
      onSuccess: () => {
        // Invalidate dashboards list to show the new dashboard
        queryClient.invalidateQueries(['dashboards'])
      },
      ...options
    }
  )
}

const createDashboardTemplate = async (token, sectionId, dashboardId, templateData) => {
  const url = `sections/${sectionId}/templates`
  const data = await apiPost({
    url,
    token,
    objectName: 'template',
    params: {
      dashboard_id: dashboardId,
      ...templateData
    }
  })
  return data
}

export function useCreateDashboardTemplate({ options = {} }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  // Extract onSuccess from options to handle it separately
  const { onSuccess: userOnSuccess, ...otherOptions } = options

  return useMutation(
    ({ sectionId, dashboardId, templateData }) => {
      return createDashboardTemplate(token, sectionId, dashboardId, templateData)
    },
    {
      onSuccess: (data, variables, context) => {
        // Invalidate all dashboardTemplates queries (with any params)
        // This ensures that queries like ['dashboardTemplates', { section_id: ... }] are refreshed
        queryClient.invalidateQueries(['dashboardTemplates'])

        // Call the user-provided onSuccess if it exists
        if (userOnSuccess) {
          userOnSuccess(data, variables, context)
        }
      },
      ...otherOptions
    }
  )
}
